import { useTheme } from "@mui/material/styles"
import { PointerEvent as ReactPointerEvent, useSyncExternalStore } from "react"
import type { AnySceneObjectT } from "tweenly"
import {
    canDrop,
    draggedObjectIds,
    DropPlacementT,
    dropPlacementAt,
    PARENT_OBJECT_TYPES,
} from "../helpers/objectsTreeDrag.helpers"
import {
    autoScrollSpeed,
    createTreeDragImage,
    DRAG_IMAGE_OFFSET,
    scrollParentOf,
} from "../helpers/objectsTreeDragImage.helpers"
import { suppressNextClick } from "../helpers/dragClick.helpers"
import { measureDropPositions } from "../helpers/objectsTreeDropPosition.helpers"
import { selectUndoable } from "../helpers/selector.helpers"
import { moveObjectsAction } from "../store/slices/objects.slice"
import { setGlobalCursor } from "./editor/useHoldDrag"
import useDynamicRefs from "./useDynamicRefs"
import { useAppDispatch, useAppStore } from "./useRedux"
import { useTimelineActions } from "./useTimelineActions"

/* ---------- the drag in progress, outside Redux: pointermove fires dozens of times a second ---------- */

type DropTargetT = { id: string; placement: DropPlacementT }

/** How far the pointer moves, pressed, before a press becomes a drag (a click stays a click). */
const DRAG_THRESHOLD = 4

/** The attribute that tells, under the pointer, which object a row belongs to. */
const ROW_ATTRIBUTE = "data-objects-tree-row"

/** The dragged rows, dimmed while they are carried. */
let carriedIds: string[] = []
let dropTarget: DropTargetT | null = null
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((listener) => listener())

const setDropTarget = (next: DropTargetT | null) => {
    if (next?.id === dropTarget?.id && next?.placement === dropTarget?.placement) return
    dropTarget = next
    notify()
}

const setCarriedIds = (ids: string[]) => {
    carriedIds = ids
    notify()
}

/**
 * Swallow the click of the release that follows a drag cancelled with Escape: the button is
 * still down, and releasing it over a nested row would click its parent row (the common
 * ancestor) and change the selection. A new press means the release went outside the window —
 * nothing left to swallow.
 */
const suppressClickOnRelease = () => {
    const onRelease = () => {
        stop()
        suppressNextClick()
    }
    const stop = () => {
        window.removeEventListener("pointerup", onRelease, true)
        window.removeEventListener("pointerdown", stop, true)
    }
    window.addEventListener("pointerup", onRelease, true)
    window.addEventListener("pointerdown", stop, true)
}

const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

/**
 * Drag & drop of objects in the objects tree, for one row: the props that let the row be picked
 * up, where (if at all) the current drag would land on it — for the row to draw the indicator —
 * and whether the row is being carried. See objectsTreeDrag.helpers for the rules.
 *
 * Built on pointer events rather than the browser's own drag & drop, which does not let the
 * page set the cursor: while objects are carried the cursor is "grabbing" everywhere, until they
 * are dropped (or Escape cancels the drag). A picture of all of the dragged objects follows the
 * pointer (createTreeDragImage), their rows stay where they are, dimmed, and the tree scrolls
 * when the pointer nears its top or bottom edge.
 *
 * @param disabled the row cannot be picked up (it is locked, or inside a locked object)
 */
export const useObjectsTreeRowDrag = (object: AnySceneObjectT, disabled: boolean) => {
    const dispatch = useAppDispatch()
    const store = useAppStore()
    const { refreshMasterTimeline } = useTimelineActions()
    const theme = useTheme()
    const [getRef] = useDynamicRefs<HTMLElement>()

    // only the row the drag is over re-renders, and only when its placement changes
    const placement = useSyncExternalStore(subscribe, () =>
        dropTarget?.id === object.id ? dropTarget.placement : null
    )

    // a row is dimmed while it is carried, together with everything inside it
    const isCarried = useSyncExternalStore(subscribe, () => carriedIds.includes(object.id))

    const objectsById = () => selectUndoable(store.getState()).objects.value

    /**
     * Where — and with what transforms — the objects that change parent have to sit under the
     * new one to look exactly as they do on the canvas — measured now, while the canvas still shows them where they are.
     */
    const measurePositions = (ids: string[], target: DropTargetT) => {
        const all = objectsById()
        const targetObject = all[target.id]
        const parentId = target.placement === "inside" ? target.id : targetObject?.parentId
        const parentElement = getRef(parentId ?? "canvas")?.current
        if (!targetObject || !parentElement) return undefined
        const changingParent = ids
            .map((id) => all[id])
            .filter((moved) => moved && moved.parentId !== parentId)
        return measureDropPositions(
            changingParent,
            all,
            parentId ?? null,
            (id) => getRef(id)?.current,
            parentElement
        )
    }

    const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
        if (disabled || event.button !== 0) return
        // the row's buttons and fields do what they do — they do not pick the row up
        if ((event.target as HTMLElement).closest("button, input, textarea, select, a")) return
        // the innermost row under the pointer is the one picked up
        event.stopPropagation()

        const startX = event.clientX
        const startY = event.clientY
        let pointerX = startX
        let pointerY = startY
        let ids: string[] = []
        let image: HTMLElement | null = null
        let scrollParent: HTMLElement | null = null
        let scrollFrame = 0

        /** Which row, and where on it, the pointer is over — if the objects may land there. */
        const findDropTarget = () => {
            const row = document
                .elementFromPoint(pointerX, pointerY)
                ?.closest<HTMLElement>(`[${ROW_ATTRIBUTE}]`)
            const targetId = row?.getAttribute(ROW_ATTRIBUTE)
            const target = targetId ? objectsById()[targetId] : undefined
            if (!row || !target) return setDropTarget(null)
            const rect = row.getBoundingClientRect()
            const next = dropPlacementAt(
                pointerY - rect.top,
                rect.height,
                PARENT_OBJECT_TYPES.includes(target.type)
            )
            // "after" an expanded parent: the indicator sits right above its first child, so
            // that is where the objects land — not after the whole branch
            const firstChildId = target.childIds.find((childId) => !ids.includes(childId))
            const landing: DropTargetT =
                next === "after" &&
                firstChildId &&
                document.querySelector(`[${ROW_ATTRIBUTE}="${firstChildId}"]`)
                    ? { id: firstChildId, placement: "before" }
                    : { id: target.id, placement: next }
            setDropTarget(
                canDrop(objectsById(), ids, landing.id, landing.placement) ? landing : null
            )
        }

        // near the tree's top or bottom edge it scrolls, for as long as the pointer stays there
        const autoScroll = () => {
            scrollFrame = 0
            if (!scrollParent) return
            const speed = autoScrollSpeed(pointerY, scrollParent.getBoundingClientRect())
            if (speed === 0) return
            scrollParent.scrollTop += speed
            findDropTarget()
            scrollFrame = requestAnimationFrame(autoScroll)
        }

        const startDrag = (row: HTMLElement) => {
            ids = draggedObjectIds(objectsById(), object.id, store.getState().activeObject.selected)
            if (ids.length === 0) return false
            const all = objectsById()
            image = createTreeDragImage(
                ids.map((id) => all[id]?.title ?? id),
                {
                    paper: theme.palette.background.paper,
                    text: theme.palette.text.primary,
                    secondaryText: theme.palette.text.secondary,
                    accent: theme.palette.primary.main,
                    fontFamily: String(theme.typography.fontFamily),
                }
            )
            document.body.appendChild(image)
            // the move cursor everywhere, over buttons and titles with cursors of their own
            // too, and no text selected along the way
            setGlobalCursor("move")
            document.body.style.userSelect = "none"
            scrollParent = scrollParentOf(row)
            setCarriedIds(ids)
            return true
        }

        const row = event.currentTarget

        const onMove = (moveEvent: PointerEvent) => {
            pointerX = moveEvent.clientX
            pointerY = moveEvent.clientY
            if (!image) {
                if (Math.hypot(pointerX - startX, pointerY - startY) < DRAG_THRESHOLD) return
                if (!startDrag(row)) return finish()
            }
            image!.style.transform = `translate(${pointerX - DRAG_IMAGE_OFFSET}px, ${
                pointerY - DRAG_IMAGE_OFFSET
            }px)`
            findDropTarget()
            if (!scrollFrame) autoScroll()
        }

        const onUp = () => {
            const target = dropTarget
            const wasDragging = Boolean(image)
            finish()
            if (!wasDragging) return
            // the click that ends the drag is not a click on what it was dropped on
            suppressNextClick()
            if (!target) return
            dispatch(
                moveObjectsAction({
                    ids,
                    targetId: target.id,
                    placement: target.placement,
                    positions: measurePositions(ids, target),
                })
            )
            // the canvas stacks objects by tree order: rebuild the timeline the way move up /
            // down does, once the store has the new order
            setTimeout(() => refreshMasterTimeline(), 100)
        }

        const onKeyDown = (keyEvent: KeyboardEvent) => {
            if (keyEvent.key !== "Escape" || !image) return
            keyEvent.stopPropagation()
            finish()
            suppressClickOnRelease()
        }

        // dropped or cancelled: the cursor and the rows are as usual again — moved, or where
        // they were
        const finish = () => {
            window.removeEventListener("pointermove", onMove)
            window.removeEventListener("pointerup", onUp)
            window.removeEventListener("pointercancel", finish)
            window.removeEventListener("keydown", onKeyDown, { capture: true })
            if (scrollFrame) cancelAnimationFrame(scrollFrame)
            scrollFrame = 0
            // only a drag that started took over the cursor — a plain click must not clear one
            // the canvas may be holding
            if (image) {
                setGlobalCursor(null)
                document.body.style.userSelect = ""
            }
            image?.remove()
            image = null
            setDropTarget(null)
            if (carriedIds.length) setCarriedIds([])
        }

        window.addEventListener("pointermove", onMove)
        window.addEventListener("pointerup", onUp)
        window.addEventListener("pointercancel", finish)
        window.addEventListener("keydown", onKeyDown, { capture: true })
    }

    return {
        placement,
        isCarried,
        rowProps: {
            [ROW_ATTRIBUTE]: object.id,
            onPointerDown,
        },
    }
}
